import { deleteCodeEditableField } from '../../code-data-editing';
import { EditorState } from '../types';
import { changeItem } from './change-item';
import { setSelectedItems } from './set-selected-items';

export interface DeleteActiveCodeFieldResult {
  state: EditorState;
  commitToUndoStack: boolean;
}

const normalizeActiveFieldKeys = (
  fieldKeys: Array<string | null | undefined>,
) =>
  fieldKeys.filter(
    (fieldKey, index, allFieldKeys): fieldKey is string =>
      Boolean(fieldKey) && allFieldKeys.indexOf(fieldKey) === index,
  );

export const deleteActiveCodeField = (
  state: EditorState,
  itemIds: string[],
): DeleteActiveCodeFieldResult | null => {
  if (itemIds.length !== 1) {
    return null;
  }

  const [itemId] = itemIds;
  const item = itemId ? state.undoableState.items[itemId] : null;

  if (!item || item.type !== 'code') {
    return null;
  }

  const track = state.undoableState.tracks.find((candidate) =>
    candidate.items.includes(item.id),
  );

  if (track?.locked) {
    return null;
  }

  const fieldKeys = normalizeActiveFieldKeys(
    item.codeActiveFields?.length
      ? item.codeActiveFields
      : [item.codeActiveField ?? item.shinhanPdfActiveField],
  );

  if (fieldKeys.length === 0) {
    return null;
  }

  let nextCode = item.code;
  let didDelete = false;
  let hasUnsupportedLegacyField = false;

  for (const fieldKey of fieldKeys) {
    const updatedCode = deleteCodeEditableField(nextCode, fieldKey);

    if (updatedCode) {
      nextCode = updatedCode;
      didDelete = true;
      continue;
    }

    if (!fieldKey.startsWith('data:')) {
      hasUnsupportedLegacyField = true;
    }
  }

  if (!didDelete) {
    if (!hasUnsupportedLegacyField) {
      return {
        state,
        commitToUndoStack: false,
      };
    }

    return {
      state: setSelectedItems(
        changeItem(state, item.id, (currentItem) =>
          currentItem.type === 'code'
            ? {
                ...currentItem,
                codeActiveField: null,
                codeActiveFields: null,
                shinhanPdfActiveField: null,
              }
            : currentItem,
        ),
        [item.id],
      ),
      commitToUndoStack: false,
    };
  }

  return {
    state: setSelectedItems(
      changeItem(state, item.id, (currentItem) =>
        currentItem.type === 'code'
          ? {
              ...currentItem,
              code: nextCode,
              compiledError: undefined,
              codeActiveField: null,
              codeActiveFields: null,
              shinhanPdfActiveField: null,
            }
          : currentItem,
      ),
      [item.id],
    ),
    commitToUndoStack: true,
  };
};
